import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useForm, FieldValues, FieldErrors, UseFormRegister } from 'react-hook-form';
import { IIndexable } from '../../model/IIndexable';

export enum DataOrigin {
  INPUT_DATA,
  SELECT_DATA,
}
export type InputSelectData<T extends FieldValues> = {
  dataOrigin: DataOrigin;
  setDataOrigin: Dispatch<SetStateAction<DataOrigin>>;
  selectRegister: UseFormRegister<IIndexable>;
  selectErrors: FieldErrors<IIndexable>;
  inputRegister: UseFormRegister<T>;
  inputErrors: FieldErrors<T>;
  getValue: () => T | undefined;
};

/**
 * Hook para datos de formulario que pueden provenir
 * de un <input> o de un <select> dependiendo de la opción
 * seleccionada por el usuario.
 * @param defaultDataOrigin: tipo de fuente de datos( input
 * o select) inicial. Este valor se omite si no hay
 * "existingValues" (no se puede mostrar el select si no hay
 * valores seleccionables).
 * @param existingValues: valores seleccionables en el select.
 * @param selectFormKey: clave con la que se guarda el índice
 * de opción seleccionada en el select.
 * @param initialValue: función para inicializar los datos
 * que se guardan tanto para el caso input como para el
 * select.
 */

export function useDataInputSelect<T extends FieldValues>(
  defaultDataOrigin: DataOrigin,
  existingValues: T[] | undefined,
  selectFormKey: string,
  initialValue?: (obj: IIndexable) => T
) {
  const [dataOrigin, setDataOrigin] = useState<DataOrigin>(DataOrigin.INPUT_DATA);

  const {
    register: selectRegister,
    formState: { errors: selectErrors },
    reset: selectReset,
    getValues: selectGetValues,
    trigger: selectValidate,
  } = useForm<IIndexable>({ mode: 'all' });
  const {
    register: inputRegister,
    formState: { errors: inputErrors },
    reset: inputReset,
    getValues: inputGetValues,
    trigger: inputValidate,
  } = useForm<T>({ mode: 'onBlur' });

  useEffect(() => {
    if (existingValues != null && existingValues.length > 0) setDataOrigin(defaultDataOrigin);
  }, [existingValues, defaultDataOrigin]);

  const validate = () => (dataOrigin === DataOrigin.SELECT_DATA ? selectValidate() : inputValidate());

  const getValue = () => {
    return dataOrigin === DataOrigin.SELECT_DATA
      ? existingValues?.[selectGetValues(selectFormKey)]
      : initialValue
      ? initialValue(inputGetValues())
      : inputGetValues();
  };

  const reset = () => {
    setDataOrigin(DataOrigin.INPUT_DATA);
    selectReset();
    inputReset(initialValue?.({}));
  };

  const inputSelectData: InputSelectData<T> = {
    dataOrigin,
    setDataOrigin,
    selectRegister,
    selectErrors,
    inputRegister,
    inputErrors,
    getValue,
  };

  return [inputSelectData, validate, reset] as const;
}
